import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Trash2,
  RotateCcw,
  XCircle,
  ImageIcon
} from "lucide-react";
import MainLayout from "@/components/MainLayout";

export default function Trash() {
  const navigate = useNavigate();
  const [trashedBanners, setTrashedBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);

  useEffect(() => {
    loadTrashedBanners();
  }, []);

  const loadTrashedBanners = async () => {
    try {
      const response = await axios.get(`${API}/banners/trash`);
      setTrashedBanners(response.data);
    } catch (error) {
      toast.error("Failed to load trashed banners");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (bannerId) => {
    try {
      await axios.post(`${API}/banners/${bannerId}/restore`);
      toast.success("Banner restored successfully");
      loadTrashedBanners();
    } catch (error) {
      toast.error("Failed to restore banner");
    }
  };

  const handlePermanentDelete = (bannerId) => {
    setBannerToDelete(bannerId);
    setDeleteDialogOpen(true);
  };

  const confirmPermanentDelete = async () => {
    if (!bannerToDelete) return;
    
    try {
      await axios.delete(`${API}/banners/${bannerToDelete}?permanent=true`);
      toast.success("Banner permanently deleted");
      setDeleteDialogOpen(false);
      setBannerToDelete(null);
      loadTrashedBanners();
    } catch (error) {
      toast.error("Failed to delete banner");
      setDeleteDialogOpen(false);
      setBannerToDelete(null);
    }
  };

  const handleEmptyTrash = async () => {
    if (trashedBanners.length === 0) return;
    
    try {
      for (const banner of trashedBanners) {
        await axios.delete(`${API}/banners/${banner.id}?permanent=true`);
      }
      toast.success("Trash emptied successfully");
      loadTrashedBanners();
    } catch (error) {
      toast.error("Failed to empty trash");
    }
  };

  // Calculate days remaining for each banner
  const getDaysRemaining = (deletedAt) => {
    if (!deletedAt) return 30;
    const deleted = new Date(deletedAt);
    const now = new Date();
    const diffMs = now - deleted;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, 30 - diffDays);
  };

  if (loading) {
    return (
      <MainLayout title="Trash" subtitle="Recover or permanently delete banners">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#10B981]"></div>
        </div>
      </MainLayout>
    );
  }

  const headerActions = trashedBanners.length > 0 && (
    <Button
      variant="outline"
      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
      onClick={handleEmptyTrash}
      data-testid="empty-trash-btn"
    >
      <XCircle className="w-4 h-4 mr-2" />
      Empty Trash
    </Button>
  );

  return (
    <MainLayout title="Trash" subtitle="Recover or permanently delete banners" headerActions={headerActions}>
      {trashedBanners.length === 0 ? (
        <Card className="bg-[#12121a] border-white/5">
          <CardContent className="p-12 text-center">
            <Trash2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Trash is Empty</h2>
            <p className="text-gray-400">
              Deleted banners will appear here and can be restored within 30 days
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {trashedBanners.map((banner) => {
            const daysRemaining = getDaysRemaining(banner.deleted_at);
            
            return (
              <Card 
                key={banner.id} 
                className="bg-[#12121a] border-white/5 overflow-hidden"
                data-testid={`trash-banner-${banner.id}`}
              >
                <div className="aspect-video bg-[#1a1a24] relative">
                  {banner.background_image ? (
                    <img
                      src={banner.background_image}
                      alt={banner.name}
                      className="w-full h-full object-cover opacity-60"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-gray-600" />
                    </div>
                  )}
                  
                  {/* Days remaining badge */}
                  <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                    daysRemaining <= 7 
                          ? 'bg-red-500/90 text-white' 
                          : 'bg-gray-800/90 text-gray-300'
                      }`}>
                        {daysRemaining} days left
                      </div>
                      
                      {/* Size badge */}
                      <div className="absolute bottom-2 left-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                        {banner.banner_width} x {banner.banner_height}
                      </div>
                    </div>
                    
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-white truncate">{banner.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Deleted {new Date(banner.deleted_at).toLocaleDateString()}
                      </p>
                      
                      <div className="flex gap-2 mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(banner.id)}
                          className="flex-1 border-[#10B981]/50 text-[#10B981] hover:bg-[#10B981]/10"
                          data-testid={`restore-${banner.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePermanentDelete(banner.id)}
                          className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                          data-testid={`delete-permanent-${banner.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

      {/* Permanent Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#12121a] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Permanently Delete Banner</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This action cannot be undone. The banner will be permanently removed from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmPermanentDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
